import clsx from "clsx";
import { Minus } from "lucide-react";
import { FC, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
// import { validate as uuidValidate } from "uuid";
import { Document } from "~/api/model";
import { useGetProject } from "~/api/projects/projects";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { Checkbox } from "~/components/ui/checkbox";
import { Progress } from "~/components/ui/progress";
import { Table, TableBody } from "~/components/ui/table";
import { TooltipProvider } from "~/components/ui/tooltip";
import { UserAvatar } from "~/components/ui/user-avatar";
import { useProjectParams } from "~/helpers";
import { useMobile } from "~/hooks/use-mobile";
import { dayjs, shortRelativeTime } from "~/utils/date";
import {
  extractTimestampFromBatchKey,
  getSelectableDocuments,
  groupDocumentsByBatch,
  parseBatchKey,
} from "../../helpers";
import { useDocumentSelectionStore } from "../../stores/documentSelectionStore";
import { DocumentsListItem } from "./DocumentsListItem";

type DocumentsGroupedListProps = {
  documents: Document[];
  allFilesNotProcessed: boolean;
  uploadProgress?: Record<string, number>;
  onCancelDraftUpload?: (filename: string) => void;
};

export const DocumentsGroupedList: FC<DocumentsGroupedListProps> = ({
  documents,
  allFilesNotProcessed,
  uploadProgress = {},
  onCancelDraftUpload,
}) => {
  const { organizationId, caseId } = useProjectParams();
  const { data: caseData } = useGetProject(organizationId, caseId);
  const intl = useIntl();
  const { isMobile } = useMobile();

  const groupedDocuments = useMemo(
    () => groupDocumentsByBatch(documents),
    [documents]
  );

  const calculateGroupPageCount = (documents: Document[]) => {
    return documents.reduce((acc, doc) => acc + (doc.pageCount || 0), 0);
  };

  const {
    isGroupFullySelected,
    isGroupPartiallySelected,
    toggleGroupSelection,
  } = useDocumentSelectionStore();

  // const uploadingDocuments = useMemo(() => {
  //   return groupedDocuments.reduce((acc, group) => {
  //     return acc + group.filter((doc) => !uuidValidate(doc.id)).length;
  //   }, 0);
  // }, [groupedDocuments]);

  // Find the latest accordion with uploading documents
  // const latestUploadingGroupIndex = useMemo(() => {
  //   if (uploadingDocuments === 0) return -1;
  //
  //   let latestIndex = -1;
  //   let latestTimestamp = -1;
  //
  //   groupedDocuments.forEach((group, index) => {
  //     const hasUploadingDocs = group.some((doc) => !uuidValidate(doc.id));
  //
  //     if (hasUploadingDocs && group[0].batchKey) {
  //       // Extract timestamp from batch key
  //       const batchKeyParts = group[0].batchKey.split("_");
  //       if (batchKeyParts.length >= 2) {
  //         const timestamp = parseInt(batchKeyParts[1]);
  //         if (timestamp > latestTimestamp) {
  //           latestTimestamp = timestamp;
  //           latestIndex = index;
  //         }
  //       }
  //     }
  //   });
  //
  //   return latestIndex;
  // }, [groupedDocuments, uploadingDocuments]);

  return (
    <TooltipProvider>
      <Accordion
        type="multiple"
        defaultValue={groupedDocuments.map((_, index) => `group-${index}`)}
        className="w-full mb-9"
      >
        {groupedDocuments.map((group, index) => {
          const selectableDocuments = getSelectableDocuments(
            group,
            allFilesNotProcessed,
            caseData
          );
          const hasSelectableDocuments = selectableDocuments.length > 0;
          const isFullySelected =
            hasSelectableDocuments &&
            isGroupFullySelected(group, allFilesNotProcessed, caseData);
          const isPartiallySelected = isGroupPartiallySelected(
            group,
            allFilesNotProcessed,
            caseData
          );

          // Check if this is the latest accordion with uploading documents
          // const isLatestUploadingGroup = index === latestUploadingGroupIndex;
          const isLatestUploadingGroup = index === 0;

          // Calculate group-specific counts
          // const groupTotalDocuments = group.length;
          // const groupUploadingDocuments = group.filter(
          //   (doc) => !uuidValidate(doc.id)
          // ).length;
          // const groupCompletedDocuments =
          //   groupTotalDocuments - groupUploadingDocuments;

          // Calculate real upload progress for the group
          const progress = Object.values(uploadProgress);
          const uploadingDocumentsCount = progress.length;
          const uploadedDocumentsCount = progress.filter(
            (p) => p == 100
          ).length;
          const groupUploadingProgress =
            uploadingDocumentsCount > 0
              ? progress.reduce((sum, p) => {
                  return sum + p;
                }, 0) / uploadingDocumentsCount
              : 100;

          // console.log('groupUploadingProgress', groupUploadingProgress)
          // groupUploadingDocuments > 0
          //   ? (() => {
          //       let totalProgress = 0;
          //       let progressCount = 0;
          //
          //       group.forEach((doc) => {
          //         if (!uuidValidate(doc.id)) {
          //           // Uploading document - use real progress
          //           const progress = uploadProgress[doc.fileHash ? doc.fileHash : doc.filename] || 0;
          //
          //           totalProgress += Math.max(0, progress); // Ignore negative values (errors)
          //           progressCount++;
          //         } else {
          //           // Completed document - 100% progress
          //           totalProgress += 100;
          //           progressCount++;
          //         }
          //       });
          //     console.log('uploadProgress', uploadProgress)
          //       return progressCount > 0
          //         ? Math.round(totalProgress / progressCount)
          //         : 0;
          //     })()
          //   : null;

          return (
            <AccordionItem
              key={`group-${index}`}
              value={`group-${index}`}
              className="relative"
            >
              <div className="flex items-center bg-muted border-b border-border relative group">
                <div
                  className={clsx(
                    "pl-4",
                    hasSelectableDocuments
                      ? "cursor-pointer"
                      : "cursor-not-allowed"
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (hasSelectableDocuments) {
                      toggleGroupSelection(
                        group,
                        allFilesNotProcessed,
                        caseData
                      );
                    }
                  }}
                >
                  <GroupCheckbox
                    checked={isFullySelected}
                    indeterminate={isPartiallySelected}
                    disabled={!hasSelectableDocuments}
                  />
                </div>
                <AccordionTrigger className="flex-1 w-full relative">
                  <div
                    className={clsx(
                      "h-[52px] p-4 justify-start items-center flex w-full",
                      isMobile ? "gap-2" : "gap-4"
                    )}
                  >
                    {group[0].author ? (
                      <div className="flex items-center gap-2 min-w-0 flex-shrink">
                        <UserAvatar name={group[0].author.name} size="sm" />
                        <span
                          className={clsx(
                            "text-muted-foreground text-sm font-medium",
                            isMobile && "truncate max-w-[100px]"
                          )}
                        >
                          {group[0].author.name}
                        </span>
                      </div>
                    ) : null}
                    <div
                      className={clsx(
                        "text-muted-foreground text-sm flex-shrink-0",
                        !isMobile && "mr-8"
                      )}
                    >
                      {index == 0 &&
                      uploadedDocumentsCount != uploadingDocumentsCount ? (
                        <span className="text-sm font-semibold flex items-center gap-2">
                          Uploading: {uploadedDocumentsCount} of{" "}
                          {uploadingDocumentsCount} completed
                        </span>
                      ) : (
                        <>
                          {/* Short timestamp - visible when not hovering */}
                          <span className="cursor-default group-hover:hidden">
                            {group[0].batchKey
                              ? shortRelativeTime(
                                  extractTimestampFromBatchKey(
                                    group[0].batchKey
                                  ),
                                  intl.locale
                                )
                              : shortRelativeTime(
                                  group[0].createdAt,
                                  intl.locale
                                )}
                          </span>
                          {/* Full timestamp - visible on hover */}
                          <span className="cursor-default hidden group-hover:block">
                            {group[0].batchKey
                              ? parseBatchKey(group[0].batchKey, intl.locale)
                                  .now
                              : dayjs(group[0].createdAt).format(
                                  "MM/DD/YYYY, HH:mm"
                                )}
                          </span>
                        </>
                      )}
                    </div>
                    {/* Add flex spacer to push content to right */}
                    <div className="flex-1"></div>
                    {/* Fixed width container to align with Pages column - hidden on mobile */}
                    {!isMobile && (
                      <div className="w-[200px] flex justify-end pr-8">
                        {/* Show page and file counts on hover */}
                        <div className="hidden group-hover:block text-muted-foreground text-sm text-right whitespace-nowrap">
                          <FormattedMessage
                            id="documentsList.fileCount"
                            values={{ count: group.length }}
                          />
                          {", "}
                          <FormattedMessage
                            id="documentsList.pageCount"
                            values={{
                              count: calculateGroupPageCount(group),
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </AccordionTrigger>
              </div>
              {/* Progress bar positioned directly below header component */}
              {groupUploadingProgress !== 100 && isLatestUploadingGroup && (
                <div className="h-1 bg-blue-50">
                  <Progress
                    value={groupUploadingProgress}
                    className="h-1 rounded-none bg-transparent"
                  />
                </div>
              )}
              <AccordionContent className="pb-0">
                <Table>
                  <TableBody>
                    {group.map((documentItem) => (
                      <DocumentsListItem
                        key={documentItem.id}
                        document={documentItem}
                        allFilesNotProcessed={allFilesNotProcessed}
                        onCancelDraftUpload={onCancelDraftUpload}
                      />
                    ))}
                  </TableBody>
                </Table>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </TooltipProvider>
  );
};

interface GroupCheckboxProps {
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  onCheckedChange?: () => void;
}

const GroupCheckbox: FC<GroupCheckboxProps> = ({
  checked,
  indeterminate,
  disabled,
  onCheckedChange,
}) => {
  return (
    <Checkbox
      checked={indeterminate ? "indeterminate" : checked}
      onCheckedChange={onCheckedChange}
      aria-label="Select group"
      icon={
        indeterminate ? <Minus className="h-4 w-4 text-white" /> : undefined
      }
      className={clsx(
        "data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 border shadow-none",
        indeterminate ? "bg-blue-600 border-blue-600" : "border-[#E4E4E7]",
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
      )}
      disabled={disabled}
    />
  );
};
